import { FgdsProductType, isRequiredAttachment, Product } from "@domain/product/product";
import { Project } from "@domain/project/base-project";
import { Checklist } from "@domain/project/checklist";
import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { ProjectEventType } from "@domain/project/project-event";
import { ProjectRelatedService } from "@domain/project/project-related-service";
import { ProjectRegion } from "@domain/project/translate-project-region.pipe";
import { BusinessScope } from "@lib/constants/business-scope";
import { FgdsProductListDiscountDto, ProductListDiscountSection } from "@lib/dtos/project/product-list-discount.dto";
import { ProductItem } from "@project/product-item";
import { IsArrayOfInstancesOf, IsInstanceOf } from "@utils/class-validator/class-validator-constraints";
import { Expose, Transform, Type } from "class-transformer";
import { IsBoolean, IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";
import { FgdsDirectBomEntry, FgdsDirectBomLine } from "./direct-bom/fgds-direct-bom-entry";
import { createProductListDiscountKey, ProductListDiscountKey } from "./product-list/product-list-discount-key.util";

export class FgdsProject extends Project {
  static businessScope: BusinessScope = BusinessScope.FGDS;

  @IsString()
  @IsOptional()
  public forzaId?: string;

  @IsString()
  @IsOptional()
  public region?: ProjectRegion;

  @IsBoolean()
  public showCostFlag: boolean = false;
  @IsArrayOfInstancesOf(AlarmDeviceConfiguration)
  @ValidateNested()
  @Type(() => AlarmDeviceConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "alarmDevices" })
  private readonly _alarmDevices: AlarmDeviceConfiguration[] = [];

  @IsArrayOfInstancesOf(TransmitterConfiguration)
  @ValidateNested()
  @Type(() => TransmitterConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "transmitters" })
  private readonly _transmitters: TransmitterConfiguration[] = [];

  @IsArrayOfInstancesOf(FlameDetectorConfiguration)
  @ValidateNested()
  @Type(() => FlameDetectorConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "flameDetectors" })
  private readonly _flameDetectors: FlameDetectorConfiguration[] = [];

  @IsArrayOfInstancesOf(GasWarningCenterConfiguration)
  @ValidateNested()
  @Type(() => GasWarningCenterConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "gasWarningCenters" })
  private readonly _gasWarningCenters: GasWarningCenterConfiguration[] = [];

  @IsArrayOfInstancesOf(SignalElementConfiguration)
  @ValidateNested()
  @Type(() => SignalElementConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "signalElements" })
  private readonly _signalElements: SignalElementConfiguration[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(PlasticSignConfiguration)
  @Type(() => PlasticSignConfiguration)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "plasticSigns" })
  private readonly _plasticSigns: PlasticSignConfiguration[] = [];

  @IsArrayOfInstancesOf(FgdsDirectBomEntry)
  @ValidateNested({ each: true })
  @Type(() => FgdsDirectBomEntry)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "directBomEntries" })
  private readonly _directBomEntries: FgdsDirectBomEntry[] = [];

  @IsArrayOfInstancesOf(FgdsProductListDiscountDto)
  @ValidateNested({ each: true })
  @Type(() => FgdsProductListDiscountDto)
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  @Expose({ name: "productListDiscounts" })
  private readonly _productListDiscounts: FgdsProductListDiscountDto[] = [];

  @IsInstanceOf(Checklist)
  @ValidateNested()
  @Type(() => Checklist)
  @Expose({ name: "checklist" })
  private readonly _checklist: Checklist = new Checklist();

  @IsInstanceOf(ProjectRelatedService)
  @ValidateNested()
  @Transform(({ value }) => (value instanceof ProjectRelatedService ? value : new ProjectRelatedService()), { toClassOnly: true })
  @Type(() => ProjectRelatedService)
  @Expose({ name: "assembly" })
  private readonly _assembly: ProjectRelatedService = new ProjectRelatedService();

  @IsInstanceOf(ProjectRelatedService)
  @ValidateNested()
  @Transform(({ value }) => (value instanceof ProjectRelatedService ? value : new ProjectRelatedService()), { toClassOnly: true })
  @Type(() => ProjectRelatedService)
  @Expose({ name: "installation" })
  private readonly _installation: ProjectRelatedService = new ProjectRelatedService();

  @IsInstanceOf(ProjectRelatedService)
  @ValidateNested()
  @Transform(({ value }) => (value instanceof ProjectRelatedService ? value : new ProjectRelatedService()), { toClassOnly: true })
  @Type(() => ProjectRelatedService)
  @Expose({ name: "documentation" })
  private readonly _documentation: ProjectRelatedService = new ProjectRelatedService();

  @IsInstanceOf(ProjectRelatedService)
  @ValidateNested()
  @Transform(({ value }) => (value instanceof ProjectRelatedService ? value : new ProjectRelatedService()), { toClassOnly: true })
  @Type(() => ProjectRelatedService)
  @Expose({ name: "engineering" })
  private readonly _engineering: ProjectRelatedService = new ProjectRelatedService();

  @IsInstanceOf(ProjectRelatedService)
  @ValidateNested()
  @Transform(({ value }) => (value instanceof ProjectRelatedService ? value : new ProjectRelatedService()), { toClassOnly: true })
  @Type(() => ProjectRelatedService)
  @Expose({ name: "additionalServices" })
  private readonly _additionalServices: ProjectRelatedService = new ProjectRelatedService();

  override get businessScope(): BusinessScope {
    return (this.constructor as any).businessScope ?? BusinessScope.FGDS;
  }

  get alarmDevices(): readonly AlarmDeviceConfiguration[] {
    this._alarmDevices.forEach((alarmDevice) => alarmDevice.init(this));
    return this._alarmDevices;
  }

  get transmitters(): readonly TransmitterConfiguration[] {
    this._transmitters.forEach((transmitter) => transmitter.init(this));
    return this._transmitters;
  }

  get flameDetectors(): readonly FlameDetectorConfiguration[] {
    this._flameDetectors.forEach((flameDetector) => flameDetector.init(this));
    return this._flameDetectors;
  }

  get gasWarningCenters(): readonly GasWarningCenterConfiguration[] {
    this._gasWarningCenters.forEach((gasWarningCenter) => gasWarningCenter.init(this));
    return this._gasWarningCenters;
  }

  get signalElements(): readonly SignalElementConfiguration[] {
    this._signalElements.forEach((signalElement) => signalElement.init(this));
    return this._signalElements;
  }

  get plasticSigns(): readonly PlasticSignConfiguration[] {
    this._plasticSigns.forEach((plasticSign) => plasticSign.init(this));
    return this._plasticSigns;
  }

  get directBomEntries(): readonly FgdsDirectBomEntry[] {
    return this._directBomEntries;
  }

  get productListDiscounts(): readonly FgdsProductListDiscountDto[] {
    return this._productListDiscounts;
  }

  getProductListDiscount(key: ProductListDiscountKey): number {
    return (
      this._productListDiscounts.find((discount) => createProductListDiscountKey(discount) === createProductListDiscountKey(key))
        ?.discount ?? 0
    );
  }

  setProductListDiscount(key: ProductListDiscountKey, discount: number): void {
    const existingDiscount = this._productListDiscounts.find(
      (candidate) => createProductListDiscountKey(candidate) === createProductListDiscountKey(key),
    );
    if (existingDiscount) {
      if (key.section === ProductListDiscountSection.ATTACHMENT_INSTRUCTIONS) {
        existingDiscount.ownerId = undefined;
      }
      existingDiscount.discount = discount;
    } else {
      this._productListDiscounts.push(
        Object.assign(new FgdsProductListDiscountDto(), {
          section: key.section,
          ownerId: key.section === ProductListDiscountSection.ATTACHMENT_INSTRUCTIONS ? undefined : key.ownerId,
          slot: key.slot,
          productId: key.productId,
          discount,
        }),
      );
    }
    this.publishUpdate(ProjectEventType.PRODUCT_LIST_DISCOUNT_UPDATED, key);
  }

  get checklist(): Checklist {
    return this._checklist;
  }

  get assembly(): ProjectRelatedService {
    return this._assembly;
  }

  get installation(): ProjectRelatedService {
    return this._installation;
  }

  get documentation(): ProjectRelatedService {
    return this._documentation;
  }

  get engineering(): ProjectRelatedService {
    return this._engineering;
  }

  get additionalServices(): ProjectRelatedService {
    return this._additionalServices;
  }

  get productConfigurations(): ProductConfiguration[] {
    return new Array<ProductConfiguration>().concat(
      this._alarmDevices,
      this._transmitters,
      this._flameDetectors,
      this._gasWarningCenters,
      this._signalElements,
      this._plasticSigns,
    );
  }

  addAlarmDevices(...alarmDevices: AlarmDeviceConfiguration[]) {
    this._alarmDevices.push(...alarmDevices);
    alarmDevices.forEach((config) => this.publishUpdate(ProjectEventType.ALARM_DEVICE_CONFIGURATION_ADDED, config));
  }

  getAlarmDeviceById(alarmDeviceId: string): AlarmDeviceConfiguration | undefined {
    return this._alarmDevices.find((alarmDevice) => alarmDevice.id === alarmDeviceId);
  }

  deleteAlarmDevice(alarmDevice: AlarmDeviceConfiguration) {
    this.deleteConfiguration(
      alarmDevice,
      this._alarmDevices,
      ProjectEventType.ALARM_DEVICE_CONFIGURATION_DELETED,
      (floorplan, config) => floorplan.deleteAlarmDevicesByConfig(config),
    );
  }

  addTransmitters(...transmitters: TransmitterConfiguration[]) {
    this._transmitters.push(...transmitters);
    transmitters.forEach((config) => this.publishUpdate(ProjectEventType.TRANSMITTER_CONFIGURATION_ADDED, config));
  }

  applyTransmitterConfigDefaultDetectionRange(transmitter: TransmitterConfiguration) {
    this.cachedFloorplans.forEach((floorplan) => floorplan.applyTransmitterConfigDefaultDetectionRange(transmitter));
    this.publishUpdate(ProjectEventType.TRANSMITTER_CONFIGURATION_UPDATED, transmitter);
  }

  getTransmitterById(transmitterId: string): TransmitterConfiguration | undefined {
    return this._transmitters.find((transmitter) => transmitter.id === transmitterId);
  }

  deleteTransmitter(transmitter: TransmitterConfiguration) {
    this.deleteConfiguration(
      transmitter,
      this._transmitters,
      ProjectEventType.TRANSMITTER_CONFIGURATION_DELETED,
      (floorplan, config) => floorplan.deleteTransmittersByConfig(config),
    );
  }

  addFlameDetectors(...flameDetectors: FlameDetectorConfiguration[]) {
    this._flameDetectors.push(...flameDetectors);
    flameDetectors.forEach((config) => this.publishUpdate(ProjectEventType.FLAME_DETECTOR_CONFIGURATION_ADDED, config));
  }

  applyFlameDetectorConfigDefaultDetectionLength(flameDetector: FlameDetectorConfiguration) {
    this.cachedFloorplans.forEach((floorplan) => floorplan.applyFlameDetectorConfigDefaultDetectionLength(flameDetector));
    this.publishUpdate(ProjectEventType.FLAME_DETECTOR_CONFIGURATION_UPDATED, flameDetector);
  }

  getFlameDetectorById(flameDetectorId: string): FlameDetectorConfiguration | undefined {
    return this._flameDetectors.find((flameDetector) => flameDetector.id === flameDetectorId);
  }

  deleteFlameDetector(flameDetector: FlameDetectorConfiguration) {
    this.deleteConfiguration(
      flameDetector,
      this._flameDetectors,
      ProjectEventType.FLAME_DETECTOR_CONFIGURATION_DELETED,
      (floorplan, config) => floorplan.deleteFlameDetectorsByConfig(config),
    );
  }

  addGasWarningCenters(...gasWarningCenters: GasWarningCenterConfiguration[]) {
    this._gasWarningCenters.push(...gasWarningCenters);
    gasWarningCenters.forEach((config) => this.publishUpdate(ProjectEventType.GAS_WARNING_CENTER_CONFIGURATION_ADDED, config));
  }

  getGasWarningCenterById(gasWarningCenterId: string): GasWarningCenterConfiguration | undefined {
    return this._gasWarningCenters.find((gasWarningCenter) => gasWarningCenter.id === gasWarningCenterId);
  }

  deleteGasWarningCenter(gasWarningCenter: GasWarningCenterConfiguration) {
    this.deleteConfiguration(
      gasWarningCenter,
      this._gasWarningCenters,
      ProjectEventType.GAS_WARNING_CENTER_CONFIGURATION_DELETED,
      (floorplan, config) => floorplan.deleteGasWarningCentersByConfig(config),
    );
  }

  addSignalElements(...signalElements: SignalElementConfiguration[]) {
    this._signalElements.push(...signalElements);
    signalElements.forEach((config) => this.publishUpdate(ProjectEventType.SIGNAL_ELEMENT_CONFIGURATION_ADDED, config));
  }

  getSignalElementById(signalElementId: string): SignalElementConfiguration | undefined {
    return this._signalElements.find((signalElement) => signalElement.id === signalElementId);
  }

  deleteSignalElement(signalElement: SignalElementConfiguration) {
    this.deleteConfiguration(
      signalElement,
      this._signalElements,
      ProjectEventType.SIGNAL_ELEMENT_CONFIGURATION_DELETED,
      (floorplan, config) => floorplan.deleteSignalElementsByConfig(config),
    );
  }

  addPlasticSigns(...plasticSigns: PlasticSignConfiguration[]) {
    this._plasticSigns.push(...plasticSigns);
    plasticSigns.forEach((config) => this.publishUpdate(ProjectEventType.PLASTIC_SIGN_CONFIGURATION_ADDED, config));
  }

  getPlasticSignById(plasticSignId: string): PlasticSignConfiguration | undefined {
    return this._plasticSigns.find((plasticSign) => plasticSign.id === plasticSignId);
  }

  deletePlasticSign(plasticSign: PlasticSignConfiguration) {
    this.deleteConfiguration(
      plasticSign,
      this._plasticSigns,
      ProjectEventType.PLASTIC_SIGN_CONFIGURATION_DELETED,
      (floorplan, config) => floorplan.deletePlasticSignsByConfig(config),
    );
  }

  override setPositionNumbers() {
    this._gasWarningCenters.forEach((c) => c.init(this));
    this._transmitters.forEach((c) => c.init(this));
    this._flameDetectors.forEach((c) => c.init(this));
    this._alarmDevices.forEach((c) => c.init(this));
    this._signalElements.forEach((c) => c.init(this));
    this._plasticSigns.forEach((c) => c.init(this));

    let startingPositionNumber = 1;

    this.setPositionNumber(startingPositionNumber, this._gasWarningCenters, (floorplan, config) => {
      floorplan.updateGasWarningCentersPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._gasWarningCenters);

    this.setGasWarningCenterPlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.gasWarningCenterPlaceholders);

    this.setPositionNumber(startingPositionNumber, this._transmitters, (floorplan, config) => {
      floorplan.updateTransmittersPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._transmitters);

    this.setTransmitterPlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.transmitterPlaceholders);

    this.setPositionNumber(startingPositionNumber, this._flameDetectors, (floorplan, config) => {
      floorplan.updateFlameDetectorsPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._flameDetectors);

    this.setFlameDetectorPlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.flameDetectorPlaceholders);

    this.setPositionNumber(startingPositionNumber, this._alarmDevices, (floorplan, config) => {
      floorplan.updateAlarmDevicesPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._alarmDevices);

    this.setAlarmDevicePlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.alarmDevicePlaceholders);

    this.setPositionNumber(startingPositionNumber, this._signalElements, (floorplan, config) => {
      floorplan.updateSignalElementsPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._signalElements);

    this.setSignalElementPlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.signalElementPlaceholders);

    this.setPositionNumber(startingPositionNumber, this._plasticSigns, (floorplan, config) => {
      floorplan.updatePlasticSignsPositionIds(config);
    });
    startingPositionNumber += this.countPlacedConfigurations(this._plasticSigns);

    this.setPlasticSignPlaceholderPositionIds(startingPositionNumber);
    startingPositionNumber += this.countPlaceholdersWithProductId((floorplan) => floorplan.plasticSignPlaceholders);

    this.setAdditionalEntryPositionNumbers(startingPositionNumber, this._directBomEntries);
  }

  createAndAddProductConfig(product: Product, name: string, quantity: number): void {
    const requiredAttachments: ProductItem[] = product.relationships
      .filter(isRequiredAttachment)
      .map((rel) => ProductItem.create(rel.productId));

    let config:
      | AlarmDeviceConfiguration
      | GasWarningCenterConfiguration
      | TransmitterConfiguration
      | FlameDetectorConfiguration
      | PlasticSignConfiguration
      | SignalElementConfiguration;

    switch (product.type) {
      case FgdsProductType.ALARMDEVICE:
        config = AlarmDeviceConfiguration.create(this, [product], requiredAttachments);
        this.addAlarmDevices(config);
        break;
      case FgdsProductType.GASWARNINGCENTER:
        config = GasWarningCenterConfiguration.create(name, this, product.id, requiredAttachments);
        this.addGasWarningCenters(config);
        break;
      case FgdsProductType.TRANSMITTER: {
        const transmitterConfig = TransmitterConfiguration.create(name, this, product.id, requiredAttachments);
        this.addTransmitters(transmitterConfig);
        config = transmitterConfig;
        break;
      }
      case FgdsProductType.FLAME_DETECTOR: {
        const flameDetectorConfig = FlameDetectorConfiguration.create(name, this, product.id, requiredAttachments);
        this.addFlameDetectors(flameDetectorConfig);
        config = flameDetectorConfig;
        break;
      }
      case FgdsProductType.PLASTICSIGN:
        config = PlasticSignConfiguration.create(name, this, product.id, "", "", "");
        this.addPlasticSigns(config);
        break;
      case FgdsProductType.SIGNALELEMENT:
        config = SignalElementConfiguration.create(name, this, product.id);
        this.addSignalElements(config);
        break;
      default:
        return;
    }

    config.setAdditionalProductListQuantity(quantity);
  }

  findProductConfig(productId: string): ProductConfiguration | undefined {
    return this.productConfigurations.find((config) => config.mainProductId === productId);
  }

  addDirectBomEntry(entry: FgdsDirectBomEntry): void {
    this._directBomEntries.push(entry);
    this.setPositionNumbers();
    this.publishUpdate(ProjectEventType.DIRECT_BOM_ENTRY_ADDED, entry);
  }

  setDirectBomEntryQuantity(entry: FgdsDirectBomEntry, quantity: number): void {
    const nextQuantity = Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 0;
    if (entry.quantity === nextQuantity) {
      return;
    }

    entry.quantity = nextQuantity;
    this.setPositionNumbers();
    this.publishUpdate(ProjectEventType.DIRECT_BOM_ENTRY_UPDATED, entry);
  }

  findDirectBomEntryByPartNumber(partNumber: string): FgdsDirectBomEntry | undefined {
    return this._directBomEntries.find((entry) => entry.lines.length === 1 && entry.lines[0]?.partNumber === partNumber);
  }

  findDirectBomEntryByLines(lines: FgdsDirectBomLine[]): FgdsDirectBomEntry | undefined {
    const lineKey = this.serializeDirectBomLines(lines);
    return this._directBomEntries.find((entry) => this.serializeDirectBomLines(entry.lines) === lineKey);
  }

  private serializeDirectBomLines(lines: FgdsDirectBomLine[]) {
    return [...lines]
      .sort((a, b) => a.partNumber.localeCompare(b.partNumber))
      .map((line) => `${line.partNumber}:${line.quantity}:${line.productType}`)
      .join("|");
  }

  static create(name: string, status: string, country: string): FgdsProject {
    return new FgdsProject(uuidv4(), name, status, country);
  }

  override validate(): void {}
}
